/**
 * useChatUnread — polls the chat thread list at a slow cadence and exposes
 * the sum of unread_count across all threads. Drives the nav-badge dot on
 * the Messages link. Returns 0 when the user isn't signed in.
 *
 * Cadence matches the rest of the app (30 s). On the Messages page itself,
 * the MessagesPage hook polls more aggressively and overrides this value.
 */

import { useEffect, useRef, useState } from "react";

import { useAuth } from "../contexts/AuthContext";
import { fetchChatThreads } from "../services/chatApi";

const POLL_MS = 30_000;

export function useChatUnread(): number {
  const { token, isAuthenticated } = useAuth();
  const [count, setCount] = useState(0);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    if (!isAuthenticated || !token) {
      setCount(0);
      return;
    }
    const poll = (): void => {
      fetchChatThreads(token)
        .then((d) => {
          if (cancelled.current) return;
          const total = d.threads.reduce((n, t) => n + (t.unread_count ?? 0), 0);
          setCount(total);
        })
        .catch(() => {
          // Soft-fail: leaving stale count is better than zeroing on a hiccup.
        });
    };
    poll();
    const id = window.setInterval(poll, POLL_MS);
    return () => {
      cancelled.current = true;
      window.clearInterval(id);
    };
  }, [token, isAuthenticated]);

  return count;
}
